import {IADNumberFormatting, TrunkingSite} from "../../app/types";
import {FormSection} from "../../common/components/SectionedForm";
import {useTranslation} from "react-i18next";
import React, {ReactElement, ReactNode, useCallback} from "react";
import {api} from "../../common/api";
import {useFetchData} from "../../common/servercall/useFetchData";
import {LabelValues, LabelValueType} from "../../common/components/LabelValues";
import PaperStack from "../../common/form/PaperStack";
import FormGroup from "../../common/form/FormGroup";
import axios from "axios";
import DialpadIcon from "@mui/icons-material/Dialpad";
import ClickableTooltip from "../../common/components/ClickableTooltip";
import IconButton from "@mui/material/IconButton";
import {InfoOutlined} from "@mui/icons-material";
import FormButtons from "../../common/form/FormButtons";
import SpinnableSubmitButton from "../../common/form/SpinnableSubmitButton";
import SoiPeeringNumberFormattingFormScreen from "./NumberFormattingForm";
import {formatCallingPhoneNumber} from "../../common/util/formatters";

export const useSoiPeeringNumberFormattingSection = (trunkingSite: TrunkingSite | undefined) : FormSection<IADNumberFormatting> => {
    const { t } = useTranslation()

    const fetchNumberFormatting = useCallback(
        async (productId: string | undefined): Promise<IADNumberFormatting | undefined> => {
            if (!productId) return
            const { data } = await api.get<IADNumberFormatting>(`/api/trunking/site/${productId}/soi-peering-number-formatting`)
            return data
        },
        []
    )
    const { data, isFetching, error, refetch } = useFetchData<IADNumberFormatting>(
        useCallback(() => fetchNumberFormatting(trunkingSite?.productId), [fetchNumberFormatting, trunkingSite?.productId])
    )

    const numberFormatLabelValues = useCallback(
        (value: IADNumberFormatting): LabelValueType[] => {
            return [
                {
                    label: t('trunking-sites.national-called'),
                    value: <>{t(`trunking-sites.calling-number-format.${value.nationalCalled}`, {number: formatCallingPhoneNumber(trunkingSite?.mainNumber || '', value.nationalCalled)})}</> ,
                    compNextToLabel: <ClickableTooltip title={t('trunking-sites.national-called-info')} buttonFn={(onClick) => <IconButton size="small" color="secondary" aria-label="df" component="span" onClick={onClick}>
                        <InfoOutlined fontSize={'small'}/>
                    </IconButton>}/>,
                    type: "FULL_LINE"},
                {
                    label: t('trunking-sites.national-calling'),
                    value: <>{t(`trunking-sites.calling-number-format.${value.nationalCalling}`, {number: formatCallingPhoneNumber('+3215666666', value.nationalCalling)})}</>,
                    compNextToLabel: <ClickableTooltip title={t('trunking-sites.national-calling-info')} buttonFn={(onClick) => <IconButton size="small" color="secondary" aria-label="df" component="span" onClick={onClick}>
                              <InfoOutlined fontSize={'small'}/>
                          </IconButton>}/>,
                    type: "FULL_LINE"
                },
                {
                    label: t('trunking-sites.international-calling'),
                    value: <>{t(`trunking-sites.calling-number-format.${value.internationalCalling}`, {number: formatCallingPhoneNumber('+17182222222', value.internationalCalling)}) }</> ,
                    compNextToLabel: <ClickableTooltip title={t('trunking-sites.international-calling-info')} buttonFn={(onClick) => <IconButton size="small" color="secondary" aria-label="df" component="span" onClick={onClick}>
                        <InfoOutlined fontSize={'small'}/>
                    </IconButton>}/>,
                    type: "FULL_LINE"},
            ]}, [t, trunkingSite?.mainNumber]
    )

    const onGeneralSubmit = useCallback((onSucceeded: () => void) =>
            async (formData: IADNumberFormatting) => {
                await api.put(`/api/trunking/site/${trunkingSite?.productId}/soi-peering-number-formatting`, formData)
                await refetch()
                onSucceeded();
            },
        [refetch, trunkingSite?.productId],
    )

    const buildFormButtons = useCallback( (cancelEditButton: ReactElement) => (isSubmitting: boolean) =>
            <FormButtons buttons={[<SpinnableSubmitButton label={t('general.form-update')} showSpinner={isSubmitting} />, cancelEditButton]} fullWidth={true}/>
        , [t])

    const renderForm = useCallback((numberFormatting: IADNumberFormatting, cancelEditButton: ReactElement, onSucceeded: () => void): ReactNode =>
        <SoiPeeringNumberFormattingFormScreen trunkingSite={trunkingSite} form={numberFormatting} buildFormButtons={buildFormButtons(cancelEditButton)}
                                 onSubmit={onGeneralSubmit(onSucceeded)} />, [trunkingSite, onGeneralSubmit, buildFormButtons])

    const renderSoiPeeringNumberFormatting = useCallback(
        (iadNumberFormatting: IADNumberFormatting): ReactNode => {
            return (
                <PaperStack>
                    <FormGroup key={1} fullWidth={true}>
                        <LabelValues labelValues={numberFormatLabelValues(iadNumberFormatting)} />
                    </FormGroup>
                    <></>
                </PaperStack>
            )
        },
        [ numberFormatLabelValues ]
    )

    return {
        title: t('trunking-sites.section.number-formatting'),
        data: data,
        isFetching: isFetching,
        error: !!error ?axios.isAxiosError(error) && error.response?.status === 404? `trunking-sites.no-trunk-defined` : error : null,
        refetch: refetch,
        renderDetails: renderSoiPeeringNumberFormatting,
        renderForm: renderForm,
        enabled: () => true,
        icon: () => <DialpadIcon/>
    }
}